import { Badge, Tooltip } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useClient } from "#/hook/useClient";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { withBoldValues } from "#/pages/ClientDetail/components/prv/prvText";
import type { PredictionBoard, ShipmentCard, ShipmentStopPlan, StopDrop } from "#/types/prediction";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { categoryColor, categoryName } from "../predictionUtils";

interface Props {
  stop: ShipmentStopPlan;
  card: ShipmentCard;
  board: Pick<PredictionBoard, "categories">;
}

/**
 * The items to drop at one stop: for every category the stop asked for, how
 * many were picked of how many requested, the exact items (oldest last scan
 * first, cages whole) and the cages skipped. Every item badge opens the item.
 */
const StopLoad = ({ stop, card, board }: Props) => {
  const t = usePrvTranslation();
  const { client } = useClient();
  const navigate = useNavigate();
  const name = (productId: number) => categoryName(board, productId);
  const color = (productId: number) => categoryColor(board, productId) ?? "#94a3b8";

  const dropLine = (drop: StopDrop) => {
    const line = card.predictions
      .find((prediction) => prediction.id === stop.predictionId)
      ?.lines.find((candidate) => candidate.productId === drop.productId);
    const cage = drop.picks.find((pick) => pick.itemType === "cage");
    const key =
      drop.picked === 0
        ? "predictedDeliveries.load.noneAvailable"
        : drop.picked < drop.requested
          ? "predictedDeliveries.load.short"
          : "predictedDeliveries.load.full";
    return withBoldValues(t, key, {
      requested: drop.requested,
      picked: drop.picked,
      available: drop.available,
      cages: drop.picks.filter((pick) => pick.itemType === "cage").length,
      loose: drop.picks.filter((pick) => pick.itemType !== "cage").length,
      firstCage: cage?.externalId ?? (cage ? `#${cage.itemId}` : ""),
      source: line ? t(`predictedDeliveries.load.source.${line.source}`) : "",
    });
  };

  return (
    <ul className="flex flex-col gap-3">
      {stop.drops.map((drop) => {
        const short = drop.picked < drop.requested;
        return (
          <li
            key={drop.productId}
            className="flex flex-col gap-2 rounded-md border border-emerald-300 bg-emerald-50/40 p-3"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: color(drop.productId) }}
                />
                {name(drop.productId)}
              </span>
              <span
                className={`text-base font-semibold tabular-nums ${short ? "text-orange-700" : "text-gray-900"}`}
              >
                {t("predictedDeliveries.load.pickedOf", {
                  picked: drop.picked,
                  requested: drop.requested,
                })}
              </span>
            </div>
            <p className="text-sm text-gray-700">{dropLine(drop)}</p>
            {drop.picks.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-gray-500">
                  {t("predictedDeliveries.load.itemsLabel", { count: drop.picks.length })}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {drop.picks.map((pick) => (
                    <Tooltip
                      key={pick.itemId}
                      withArrow
                      label={
                        pick.lastScanAt
                          ? t("predictedDeliveries.load.lastScan", {
                              date: formatDateTime(pick.lastScanAt, client),
                            })
                          : t("predictedDeliveries.load.neverScanned")
                      }
                    >
                      <Badge
                        variant="light"
                        size="lg"
                        className="cursor-pointer font-medium normal-case hover:brightness-95"
                        style={{
                          backgroundColor: `color-mix(in srgb, ${color(drop.productId)} 14%, white)`,
                          color: "var(--mantine-color-gray-9)",
                          border: `1px solid color-mix(in srgb, ${color(drop.productId)} 45%, white)`,
                        }}
                        onClick={() => void navigate(`/inventory/items/${pick.itemId}`)}
                      >
                        {pick.itemType === "cage"
                          ? t("predictedDeliveries.load.cagePick", {
                              id: pick.externalId ?? `#${pick.itemId}`,
                              units: pick.units,
                            })
                          : (pick.externalId ?? `#${pick.itemId}`)}
                      </Badge>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}
            {drop.skippedCages.length > 0 && (
              <span className="text-xs text-gray-500">
                {t("predictedDeliveries.load.skippedCages", {
                  ids: drop.skippedCages
                    .map((cage) => `${cage.externalId ?? `#${cage.itemId}`} (${cage.units})`)
                    .join(", "),
                })}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default StopLoad;
